import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingAPI } from '../../features/onboarding/api/onboardingAPI';

export const REDO_CONFIRM_TEXT = "You'll answer all the questions again. Your current answers stay until you replace them.";

// Route state ChatShell reads to open the onboarding chat (and then clears).
export const OPEN_ONBOARDING_CHAT = { state: { openOnboarding: true } };

/**
 * Profile → "Redo onboarding" (PLAN.md §4.3, decision U4: in the profile
 * section, not password/security). Shown only when GET /api/onboarding/state
 * says redo_available (the redo tables are migrated and onboarding is
 * complete). Confirming starts the redo and opens /chat with an explicit
 * request for the onboarding chat (an open redo alone never changes which
 * chat /chat opens). While one is open the button reads "Continue redo" and
 * goes straight there, the same way.
 */
const RedoOnboardingButton = () => {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    onboardingAPI.getState()
      .then((res) => { if (!cancelled) setState(res); })
      // A failed read leaves the button hidden: it is optional, and nothing
      // is written until the user confirms.
      .catch(() => { if (!cancelled) setState(null); });
    return () => { cancelled = true; };
  }, []);

  if (!state?.redo_available) return null;

  const redoOpen = Boolean(state.redo);

  const startRedo = async () => {
    setBusy(true);
    setError('');
    try {
      await onboardingAPI.startRedo();
    } catch (_) {
      setError('Could not start the redo. Please try again.');
      setBusy(false);
      return;
    }
    navigate('/chat', OPEN_ONBOARDING_CHAT);
  };

  return (
    <div className="form-group full-width redo-onboarding">
      <span className="redo-onboarding-title">Onboarding</span>
      <small className="redo-onboarding-hint">
        {redoOpen
          ? 'You have a redo in progress.'
          : 'Answer the setup questions again to update your profile.'}
      </small>
      {confirming ? (
        <div className="redo-onboarding-confirm" role="alertdialog" aria-label="Redo onboarding?">
          <span>{REDO_CONFIRM_TEXT}</span>
          <div className="redo-onboarding-actions">
            <button type="button" className="redo-onboarding-btn redo-onboarding-btn-solid" disabled={busy} onClick={startRedo}>
              {busy ? 'Starting…' : 'Confirm'}
            </button>
            <button type="button" className="redo-onboarding-btn" disabled={busy} onClick={() => { setConfirming(false); setError(''); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="redo-onboarding-actions">
          <button
            type="button"
            className="redo-onboarding-btn"
            onClick={() => (redoOpen ? navigate('/chat', OPEN_ONBOARDING_CHAT) : setConfirming(true))}
          >
            {redoOpen ? 'Continue redo' : 'Redo onboarding'}
          </button>
        </div>
      )}
      {error ? <p className="field-error" role="alert">{error}</p> : null}
    </div>
  );
};

export default RedoOnboardingButton;
